/**
 * Kiosk display helpers for the cart.
 *
 * The SERVER prices every order (menu prices, the promo it validated, the
 * restaurant's own tax rate): the payment screen shows the server's subtotal,
 * tax and total. Nothing here computes tax or an order total.
 */

/**
 * Sum of the cart lines at menu prices (what the cart shows before the order is priced).
 * @param {Array} cart - Array of cart items
 * @returns {number} Subtotal amount (dollars)
 */
export function calculateSubtotal(cart) {
  if (!cart || cart.length === 0) return 0;

  return cart.reduce((sum, item) => {
    const itemTotal = (item.totalPrice || item.basePrice || 0) * (item.quantity || 1);
    return sum + itemTotal;
  }, 0);
}

/**
 * The tip the customer picked as a percentage of the server's subtotal.
 * @param {number} subtotal - the priced order's subtotal (dollars)
 * @param {number} tipPercentage - e.g. 15 for 15%
 * @returns {number} Tip amount (dollars, rounded to cents)
 */
export function calculateTipFromPercentage(subtotal, tipPercentage) {
  if (!tipPercentage || tipPercentage <= 0) return 0;
  return Math.round(subtotal * (tipPercentage / 100) * 100) / 100;
}

/**
 * Tip options from the restaurant's own presets (kiosk config `payment.tip_presets`).
 * @param {number} subtotal - the priced order's subtotal (dollars)
 * @param {number[]} presets - percentages set by the owner
 */
export function getTipOptions(subtotal, presets = []) {
  return [0, ...presets.filter((p) => Number(p) > 0)].map((pct) => ({
    percentage: pct,
    amount: calculateTipFromPercentage(subtotal, pct),
    label: pct === 0 ? 'No Tip' : `${pct}%`,
  }));
}

/**
 * Calculate item price with modifiers
 * @param {number} basePrice - Item base price
 * @param {Array} modifiers - Selected modifier options with prices
 * @param {number} quantity - Item quantity
 * @returns {Object} Price breakdown
 */
export function calculateItemTotal(basePrice, modifiers = [], quantity = 1) {
  const modifierTotal = modifiers.reduce((sum, mod) => sum + (mod.price || 0), 0);
  const unitPrice = basePrice + modifierTotal;
  const lineTotal = unitPrice * quantity;

  return {
    basePrice,
    modifierTotal,
    unitPrice: Math.round(unitPrice * 100) / 100,
    quantity,
    lineTotal: Math.round(lineTotal * 100) / 100,
  };
}
